import { PetDiagnosisModel } from "@common/models";
import { DiagnosisStatus } from "@common/models/Enums";
import { observer } from "mobx-react";
import React from "react";
import { Button, Table } from "react-bootstrap";
import { useHospitalApp } from "@hospital/hooks";
import { stringDateToISODate,DateTimeUtils } from "@common/utils";
/**
 * Props for the PetProfileDiagnosesAddedTable component.
 */
export type IPetProfileDiagnosesAddedTableProps = {
  petDiagnosesAdded: PetDiagnosisModel[];
  editProfileDiagnosisFromTable: (diagnosis: PetDiagnosisModel) => void;
  removeProfileDiagnosisFromTable: (diagnosisId: number) => void;
};

/**
 * Local method used to convert the petdiagnosed date into local hospital time zone 
 * if there is T(time portion), else return the formatted date only.
 */
export const formatDate = (date: string | undefined, timeZone?: string) => {
  if (!date) return "";
  
  return date.includes("T")?
  stringDateToISODate( DateTimeUtils.getZoneDateAsLocal(date,timeZone??'UTC').toISO())
 :stringDateToISODate(date);
};

/**
 * Displays the list of diagnoses that have already been added to the pet’s profile.
 *
 * Currently, this component only renders the table header.
 * The rendering of diagnosis rows and related actions will be implemented in a future update.
 */
export const PetProfileDiagnosesAddedTable = observer(
  ({
    petDiagnosesAdded,
    editProfileDiagnosisFromTable,
    removeProfileDiagnosisFromTable,
  }: IPetProfileDiagnosesAddedTableProps) => {    
    const hospitalApp = useHospitalApp();
    return (
      <div>
        {/* Section header */}
        <div>
          <h3 className="fw-bold mt-3" style={{ fontSize: "1.25rem" }}>
            Diagnoses Added to Profile
          </h3>
        </div>
        {/* Table container */}
        <div className="overflow-auto">
          <Table hover striped size="sm" className="pet-diagnoses-details-table">
            <thead>
              <tr>
                <th className="text-start">Description</th>
                <th className="text-end pr-8">Actions</th>
              </tr>
            </thead>
            <tbody>
              {petDiagnosesAdded.map((petDiagnosis, idx) => (
                <tr key={`petDiagnosis-${idx}-row`}>
                  <td>
                    <div className="d-flex flex-column gap-1">
                      <span> {petDiagnosis.diagnosis.name} </span>
                      {
                        <div
                          className="d-flex flex-column pe-5 ps-4"
                          style={{ fontSize: "smaller" }}
                        >
                          <p className="attributes-title mb-1">
                            Date Diagnosed:{" "}
                            <span className="attributes-desc">
                              {formatDate(petDiagnosis.dateDiagnosed, hospitalApp.currentHospital?.store?.timezoneName)}
                            </span>
                          </p>
                          <p className="attributes-title mb-0">
                            Status:{" "}
                            <span className="attributes-desc text-uppercase">
                              {DiagnosisStatus[petDiagnosis.diagnosisStatusId]}
                            </span>
                          </p>
                          <p className="attributes-title mb-0">
                            Notes:{" "}
                            <span className="attributes-desc">
                              {petDiagnosis.latestPetDiagnosisStatus.notes}
                            </span>
                          </p>
                        </div>
                      }
                    </div>
                  </td>
                  <td className="text-end pe-4">
                    <Button
                      variant="outline-primary"
                      style={{ width: "5rem" }}
                      className={"pet-profile-diagnoses-item-add me-2"}
                      onClick={() =>
                        editProfileDiagnosisFromTable(petDiagnosis)
                      }
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-primary"
                      className={"pet-profile-diagnoses-item-add me-2"}
                      style={{ width: "5rem" }}
                      onClick={() =>
                        removeProfileDiagnosisFromTable(
                          petDiagnosis.diagnosisId,
                        )
                      }
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  },
);
